<script>
  import { onMount } from 'svelte';
  import { FEEDBACK_EMOJI, feedbackLines } from '../lib/feedback.js';
  import { api } from '../lib/api.js';
  import EcardViews from '../components/EcardViews.svelte';
  import { ecardBase, datasource } from '../lib/stores.js';
  import CardPageLink from '../components/CardPageLink.svelte';

  let ecards = [];
  let loading = true;
  let error = null;
  let enabled = true;
  let q = '';
  let expandedId = null;

  // Milestones (the Analytics tab's manual annotations: price change, feature
  // launches) double as "since …" filters here, so you can see exactly which
  // orders arrived after a given change. Loaded once; failure just hides the bar.
  let milestones = [];
  let sinceDate = null; // YYYY-MM-DD (local day) or null = all
  async function loadMilestones() {
    try {
      const d = await api('/analytics/milestones');
      milestones = (d.milestones || []).slice().sort((a, b) => b.date.localeCompare(a.date));
    } catch (e) {
      milestones = [];
    }
  }

  const toggleViews = (id) => (expandedId = expandedId === id ? null : id);

  onMount(() => { load(); loadMilestones(); });

  async function load() {
    loading = true;
    error = null;
    try {
      const data = await api('/ecards');
      ecards = data.ecards || [];
      enabled = data.enabled !== false;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  // "Purchased" = paidAt; admin-built / unpaid cards fall back to created.
  const purchasedTs = (e) => {
    const t = Date.parse(e.paidAt || e.created || '');
    return Number.isFinite(t) ? t : 0;
  };
  // Local calendar day as YYYY-MM-DD — the same shape milestone dates use, so
  // grouping and "since" comparisons agree with each other.
  const localDay = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const todayKey = localDay(Date.now());
  const yesterdayKey = localDay(Date.now() - 86400000);
  function dayHeading(key) {
    if (!key) return 'No purchase date';
    const label = new Date(key + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    if (key === todayKey) return `Today · ${label}`;
    if (key === yesterdayKey) return `Yesterday · ${label}`;
    return label;
  }
  const msLabel = (m) => (m.detail.length > 26 ? m.detail.slice(0, 25) + '…' : m.detail);
  const fmtDay = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  function fmtDate(s) {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleDateString('en-GB', { dateStyle: 'medium' });
    } catch (e) {
      return s;
    }
  }

  // pending | paid | sent | failed | bounced
  const statusClass = (s) => ({
    sent: 'ok', paid: 'info', pending: 'wait', failed: 'bad', bounced: 'bad',
  }[s] || 'wait');

  // Twilio carrier states; greens for delivered/sent, reds for failures.
  const smsClass = (s) => ({
    delivered: 'ok', sent: 'ok', accepted: 'info', sending: 'wait', queued: 'wait',
    undelivered: 'bad', failed: 'bad',
  }[s] || 'wait');

  const recipientContact = (e) =>
    e.delivery === 'sms' ? e.recipientPhone : e.delivery === 'email' ? e.recipientEmail : null;

  // Click attribution (from the Shopify order's customer journey, via UTM tags
  // on ad URLs). kind: paid | organic | referral | direct | unknown.
  const srcTitle = (a) => {
    if (!a || a.kind === 'unknown') return 'No journey recorded — consent declined, ad blocker, or pre-tracking order';
    return 'Cookie (Shopify journey) attribution\n' + ([
      a.campaign && `Campaign: ${a.campaign}`,
      a.term && `Keyword: ${a.term}`,
      a.query && `Search query: ${a.query}`,
      a.landingPage && `Landed: ${a.landingPage}`,
      a.referrerUrl && !a.referrerUrl.includes('cardhero.com') && `Referrer: ${a.referrerUrl}`,
    ].filter(Boolean).join('\n') || a.channel);
  };

  // Cookieless (_chs) attribution — the encoded UTM blob passed through URLs
  // by the theme/builder. Survives consent declines and ad blockers.
  const urlTitle = (u) => 'Cookieless (URL) attribution\n' + [
    u.campaign && `Campaign: ${u.campaign}`,
    u.keyword && `Keyword: ${u.keyword}${u.matchType ? ` (${{ e: 'exact', p: 'phrase', b: 'broad' }[u.matchType] || u.matchType})` : ''}`,
    u.query && `Search query: ${u.query}`,
    u.clickKind && `Click id: ${u.clickKind}`,
    u.clickedAt && `Ad clicked: ${new Date(u.clickedAt).toLocaleString('en-GB')}`,
  ].filter(Boolean).join('\n');

  // Cookieless site-visit attribution — the non-ad visit log (Firestore
  // site_visits, live 2026-10-06), stamped with the order by the Ecard webhook.
  // Third fallback: credits organic/direct/referral orders whose cookie journey
  // was never recorded.
  const visitTitle = (v) => 'Cookieless (site visit) attribution\n' + [
    v.referrerHost && `Referrer: ${v.referrerHost}`,
    v.campaign && `Campaign: ${v.campaign}`,
    v.landing && `Landed: ${v.landing}`,
    v.firstAt && `Visit started: ${new Date(v.firstAt).toLocaleString('en-GB')}`,
  ].filter(Boolean).join('\n');

  // Summary strip: channel counts over the last 30 days — or, when a "since
  // milestone" filter is on, since that day — across all loaded ecards
  // (ignoring the text search), plus the tracked share — the % of orders with a recorded
  // journey. That share is a proxy for cookie-banner opt-in (minus ad blockers
  // / ITP), so it's the number to watch when testing consent-banner changes.
  $: summary = (() => {
    const cutoff = sinceDate ? new Date(sinceDate + 'T00:00:00').getTime() : Date.now() - 30 * 86400000;
    const counts = new Map();
    let total = 0, tracked = 0, urlTagged = 0, rescued = 0, visitTagged = 0, visitRescued = 0, reminders = 0, replies = 0, codes = 0, newsletters = 0;
    let opened = 0, invites = 0, forms = 0; // Say-thanks funnel (recipient-confirmed opens only)
    let leads = 0, phone = 0, tablet = 0, desktop = 0; // story-card leads; device the recipient opened on
    for (const e of ecards) {
      const ts = purchasedTs(e);
      if (!ts || ts < cutoff) continue;
      if (e.reminderRequested) reminders += 1;
      if (e.recipientViewedAt) opened += 1;
      leads += e.leadCount || 0;
      if (e.recipientViewport) { if (e.recipientViewport.class === 'phone') phone += 1; else if (e.recipientViewport.class === 'tablet') tablet += 1; else desktop += 1; }
      if (e.thanksInviteSeenAt) invites += 1;
      if (e.thanksComposeOpenedAt) forms += 1;
      if (e.thanksRepliedAt) replies += 1;
      if (e.thanksCodeEmailedAt) codes += 1;
      if (e.thanksNewsletterConfirmedAt) newsletters += 1;
      const a = e.attribution;
      if (!a) continue; // no linked order (admin-built/unpaid) — not a consent signal
      total += 1;
      if (a.kind !== 'unknown') tracked += 1;
      if (a.url) {
        urlTagged += 1;
        if (a.kind === 'unknown') rescued += 1; // cookieless caught what cookies missed
      }
      if (a.visit) {
        visitTagged += 1;
        if (a.kind === 'unknown' && !a.url) visitRescued += 1; // site-visit log caught an organic/direct order
      }
      // Channel counts prefer the journey, then the URL blob, then the site
      // visit — so a consent-declined Bing order still counts as Bing, and a
      // consent-declined organic order counts as organic rather than Unknown.
      const eff = a.kind !== 'unknown' ? a : (a.url || a.visit || a);
      const cur = counts.get(eff.channel) || { n: 0, kind: eff.kind };
      cur.n += 1;
      counts.set(eff.channel, cur);
    }
    const channels = [...counts.entries()]
      .map(([channel, { n, kind }]) => ({ channel, n, kind }))
      .sort((a, b) => b.n - a.n);
    return {
      channels, total, reminders, replies, codes, newsletters, urlTagged, rescued, visitTagged, visitRescued, opened, invites, forms, leads, phone, tablet, desktop,
      trackedPct: total ? Math.round((100 * tracked) / total) : null,
      label: sinceDate ? `Since ${fmtDay(sinceDate)}:` : 'Last 30 days:',
      period: sinceDate ? `since ${fmtDay(sinceDate)}` : 'the last 30 days',
    };
  })();

  let copiedId = null;
  async function copyId(id) {
    try {
      await navigator.clipboard.writeText(id);
      copiedId = id;
      setTimeout(() => { if (copiedId === id) copiedId = null; }, 1200);
    } catch (e) {
      /* clipboard unavailable; ignore */
    }
  }

  // ---- Admin edit modal ---------------------------------------------------
  // Mirrors the Editor's orders-tab edit: fields shown adapt to delivery
  // (email/sms) and mode (typed/handwritten). Saving PATCHes the ecard in the
  // CURRENTLY-SELECTED data source (prod/staging) via the api() wrapper.
  const STATUSES = ['pending', 'paid', 'sent', 'failed', 'bounced'];
  let editing = null;      // a shallow copy of the row being edited
  let savingEdit = false;
  let editError = '';

  function openEdit(e) {
    editing = { ...e, _origStatus: e.status };
    editError = '';
  }
  function closeEdit() {
    editing = null;
    editError = '';
    savingEdit = false;
  }

  let deleting = false;
  // Permanent delete from the edit modal — for test entries that clutter the
  // queue. Two-step confirm: the dialog spells out exactly what goes, and warns
  // when the card belongs to a real Shopify order or has already been sent.
  async function deleteEcard() {
    if (!editing || deleting) return;
    const e = editing;
    const target = e.delivery === 'sms' ? e.recipientPhone : e.recipientEmail;
    const lines = [
      `Permanently delete this ecard from ${$datasource.toUpperCase()}?`,
      '',
      `Order: ${e.orderNumber || 'none'}  ·  Status: ${e.status || '—'}`,
      `To: ${e.recipientName || '—'} (${target || e.delivery || '—'})`,
      `From: ${e.customerEmail || '—'}`,
    ];
    if (e.orderId) lines.push('', '⚠️ Linked to a real Shopify order. The order itself is not touched, but the ecard and its view history are gone for good.');
    if (e.status === 'sent') lines.push('', '⚠️ This card has already been sent — the recipient\'s link will stop working.');
    lines.push('', 'There is no undo.');
    if (!confirm(lines.join('\n'))) return;
    deleting = true;
    editError = '';
    try {
      await api(`/ecards/${e.id}`, { method: 'DELETE' });
      ecards = ecards.filter((x) => x.id !== e.id);
      closeEdit();
    } catch (err) {
      editError = err.message;
    } finally {
      deleting = false;
    }
  }

  async function saveEdit() {
    if (!editing) return;
    // Flipping to 'paid' re-queues a real recipient send — confirm first.
    if (editing.status === 'paid' && editing.status !== editing._origStatus) {
      const target = editing.delivery === 'sms' ? editing.recipientPhone : editing.recipientEmail;
      if (!confirm(`Set status to "paid"? This re-queues delivery to ${target || 'the recipient'} on the next scheduled run.`)) return;
    }
    savingEdit = true;
    editError = '';
    const payload = { status: editing.status };
    if (editing.delivery === 'email') {
      payload.recipientName = editing.recipientName;
      payload.recipientEmail = editing.recipientEmail;
      payload.sendDate = editing.sendDate;
      if (editing.sendTime) payload.sendTime = editing.sendTime;
    } else if (editing.delivery === 'sms') {
      payload.recipientName = editing.recipientName;
      payload.recipientPhone = editing.recipientPhone;
      payload.sendDate = editing.sendDate;
      if (editing.sendTime) payload.sendTime = editing.sendTime;
    }
    if (editing.mode === 'typed') {
      payload.message = editing.message;
      if (editing.font) payload.font = editing.font;
      if (editing.fontSize) payload.fontSize = editing.fontSize;
      if (editing.textColour) payload.textColour = editing.textColour;
    } else if (editing.mode === 'handwritten') {
      if (editing.inkColour) payload.inkColour = editing.inkColour;
    }
    try {
      await api(`/ecards/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      closeEdit();
      await load();
    } catch (err) {
      editError = err.message;
      savingEdit = false;
    }
  }

  $: filtered = (!q.trim() && !sinceDate ? ecards : ecards.filter((e) => {
    if (sinceDate && localDay(purchasedTs(e)) < sinceDate) return false;
    if (!q.trim()) return true;
    const hay = [e.id, e.shortId, e.orderNumber, e.productTitle, e.audioLabel, e.customerEmail, e.recipientName,
      e.recipientEmail, e.recipientPhone, e.status, e.nudgedAt ? 'nudged' : '', e.editCount ? 'edited' : '',
      e.attribution && e.attribution.channel, e.attribution && e.attribution.campaign,
      e.attribution && e.attribution.url && e.attribution.url.channel,
      e.attribution && e.attribution.url && e.attribution.url.campaign,
      e.attribution && e.attribution.visit && e.attribution.visit.channel,
      e.attribution && e.attribution.visit && e.attribution.visit.referrerHost,
      e.reminderRequested ? 'reminder' : '', e.thanksInviteSeenAt ? 'invite' : '', e.thanksComposeOpenedAt ? 'form' : '', e.thanksRepliedAt ? 'replied' : '', e.thanksCodeEmailedAt ? 'code' : '', e.thanksNewsletter ? 'newsletter' : '', e.sentNotifiedAt ? 'told sent' : '', e.openedNoticeAt ? 'told opened' : '', e.feedbackRating ? 'feedback ' + e.feedbackRating : '', e.storyPageCount ? 'story' : '', e.leadCount ? 'lead' : '', e.recipientViewport ? e.recipientViewport.class : '', ...feedbackLines(e.feedback)]
      .filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  })).slice().sort((a, b) => purchasedTs(b) - purchasedTs(a));

  // Rows grouped under one heading per purchase day (newest first). Unpaid
  // cards with no date sink to the bottom under their own heading.
  $: groups = (() => {
    const out = [];
    let cur = null;
    for (const e of filtered) {
      const key = localDay(purchasedTs(e));
      if (!cur || cur.key !== key) { cur = { key, rows: [] }; out.push(cur); }
      cur.rows.push(e);
    }
    return out;
  })();
</script>

<div class="head">
  <div class="left">
    <h2>Ecards {#if !loading && !error}<span class="count">{filtered.length}</span>{/if}</h2>
    <input class="search" placeholder="Filter…" bind:value={q} />
  </div>
  <button on:click={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
</div>

{#if milestones.length}
  <div class="sincebar">
    <span class="sincebar-label">Purchased:</span>
    <span class="presets">
      <button class:on={sinceDate === null} on:click={() => (sinceDate = null)}>all</button>
      {#each milestones as m (m.id)}
        <button class:on={sinceDate === m.date} class="ms" title={`${m.detail} — milestone ${m.date}, set in Analytics`} on:click={() => (sinceDate = sinceDate === m.date ? null : m.date)}>📌 since {msLabel(m)} ({fmtDay(m.date)})</button>
      {/each}
    </span>
    {#if sinceDate}
      <span class="sincebar-meta">{filtered.length} order{filtered.length === 1 ? '' : 's'} since {fmtDay(sinceDate)}</span>
    {/if}
  </div>
{/if}

{#if !loading && !error && enabled && (summary.channels.length || summary.reminders)}
  <div class="src-summary">
    <span class="src-summary-label">{summary.label}</span>
    {#each summary.channels as s (s.channel)}
      <span class="badge src-{s.kind}">{s.channel} <strong>{s.n}</strong></span>
    {/each}
    {#if summary.trackedPct !== null}
      <span class="badge tracked" title="Share of the {summary.total} orders {summary.period} with a recorded journey — proxy for cookie-consent opt-in (minus ad blockers)">📡 {summary.trackedPct}% tracked</span>
    {/if}
    {#if summary.urlTagged}
      <span class="badge url-tagged" title="Orders carrying the cookieless _chs URL tag; {summary.rescued} of them had no cookie journey (attribution rescued)">🔗 {summary.urlTagged} URL-tagged{summary.rescued ? ` (+${summary.rescued} rescued)` : ''}</span>
    {/if}
    {#if summary.visitTagged}
      <span class="badge visit-tagged" title="Orders matched to a cookieless site visit (non-ad visit log, live from 6 Oct 2026); {summary.visitRescued} of them had neither a cookie journey nor a URL tag (attribution rescued)">👣 {summary.visitTagged} visit-matched{summary.visitRescued ? ` (+${summary.visitRescued} rescued)` : ''}</span>
    {/if}
    {#if summary.reminders}
      <span class="badge reminder" title="Reminder opt-ins across ecards bought {summary.period}">🔔 {summary.reminders} reminder{summary.reminders === 1 ? '' : 's'}</span>
    {/if}
    {#if summary.invites || summary.forms}
      <span class="badge funnel" title="Say-thanks funnel, ecards bought {summary.period}: recipient opened the card → turned to the back cover (saw the invite) → opened the reply form → replied. Back-cover markers live from 2026-09-14.">👀 {summary.opened} opened → {summary.invites} saw invite → {summary.forms} opened form → {summary.replies} replied</span>
    {/if}
    {#if summary.replies}
      <span class="badge replied" title="Recipients who sent a thank-you reply (ecards bought {summary.period})">💌 {summary.replies} repl{summary.replies === 1 ? 'y' : 'ies'}</span>
    {/if}
    {#if summary.leads}
      <span class="badge lead" title="Lead-form submissions on brand story cards (ecards bought {summary.period})">🧲 {summary.leads} lead{summary.leads === 1 ? '' : 's'}</span>
    {/if}
    {#if summary.phone || summary.tablet || summary.desktop}
      <span class="badge device" title="Device the recipient first opened on, by viewport width (recorded from 16 Oct 2026; ecards bought {summary.period})">📱 {summary.phone} phone{#if summary.tablet} · 📟 {summary.tablet} tablet{/if} · 💻 {summary.desktop} desktop</span>
    {/if}
    {#if summary.codes}
      <span class="badge code" title="Recipients who asked for the THANKYOU15 code by email (ecards bought {summary.period})">🎟️ {summary.codes} code{summary.codes === 1 ? '' : 's'}</span>
    {/if}
    {#if summary.newsletters}
      <span class="badge newsletter" title="Recipients who confirmed the newsletter opt-in (ecards bought {summary.period})">📣 {summary.newsletters} confirmed opt-in{summary.newsletters === 1 ? '' : 's'}</span>
    {/if}
  </div>
{/if}

{#if loading}
  <p class="muted">Loading ecards…</p>
{:else if !enabled}
  <p class="muted">Ecard data isn't configured on this deployment. Set <code>GCS_CREDENTIALS</code> (or <code>firestore.keyFile</code>) to enable.</p>
{:else if error}
  <p class="error">⚠️ {error}</p>
{:else if filtered.length === 0}
  <p class="muted">{sinceDate ? `No ecard orders since ${fmtDay(sinceDate)}.` : 'No ecard orders found.'}</p>
{:else}
  <table>
    <thead>
      <tr>
        <th>Order</th><th>ID</th><th>Design</th><th>Customer</th><th>Delivery</th>
        <th>Recipient</th><th>Send</th><th>Status</th><th>Source</th><th class="num">Views</th><th>Paid</th><th></th>
      </tr>
    </thead>
    {#each groups as g (g.key)}
    <tbody class="day">
      <tr class="day-head">
        <th colspan="12">
          <span class="day-title">{dayHeading(g.key)}</span>
          <span class="day-count">{g.rows.length} order{g.rows.length === 1 ? '' : 's'}</span>
        </th>
      </tr>
      {#each g.rows as e (e.id)}
        <tr>
          <td class="mono">
            {#if e.orderNumber}
              <a href="#/order/{String(e.orderNumber).replace(/^#/, '')}" class="order-link">{e.orderNumber}</a>
            {:else}
              —
            {/if}
          </td>
          <td>
            <button class="id-btn" on:click={() => copyId(e.id)} title="Click to copy: {e.id}">
              <span class="mono-id">{e.shortId || e.id}</span>
              <span class="copy-hint">{copiedId === e.id ? '✓ copied' : '⧉'}</span>
            </button>
          </td>
          <td>
            <div>{e.productTitle || e.productId || '—'}</div>
            {#if e.audioLabel}<div class="sub music" title="Sound chosen in the builder">♪ {e.audioLabel}</div>{/if}
            <a class="preview" href={`${$ecardBase}/ecard/${e.id}?crdv=a`} target="_blank" rel="noopener">Preview ↗</a>
            <div class="sub"><CardPageLink ecard={e} compact /></div>
          </td>
          <td>{e.customerEmail || '—'}</td>
          <td>{#if e.delivery}<span class="pill">{e.delivery}</span>{:else}—{/if}</td>
          <td>
            <div>{e.recipientName || '—'}</div>
            {#if recipientContact(e)}<div class="sub">{recipientContact(e)}</div>{/if}
          </td>
          <td>
            <div>{e.sendDate || '—'}</div>
            {#if e.sendTime}<div class="sub">{e.sendTime}</div>{/if}
          </td>
          <td>
            <span class="badge {statusClass(e.status)}">{e.status || '—'}</span>
            {#if e.delivery === 'sms' && e.smsStatus}
              <span class="badge sm {smsClass(e.smsStatus)}" title={e.smsErrorCode ? 'Twilio error ' + e.smsErrorCode : ''}>{e.smsStatus}</span>
            {/if}
            {#if e.sentNotifiedAt}
              <span class="badge sm toldsent" title="Sender emailed that their scheduled card went out {fmtDate(e.sentNotifiedAt)}">📤 told sent</span>
            {:else if e.sentNotifyError}
              <span class="badge sm toldsent-err" title="The 'sent' email to the sender failed: {e.sentNotifyError}">⚠️ sent email failed</span>
            {/if}
            {#if e.nudgedAt}
              <span class="badge sm nudge" title="Purchaser nudged {fmtDate(e.nudgedAt)}">👋 nudged</span>
            {/if}
            {#if e.openedNoticeAt}
              <span class="badge sm told" title="Sender emailed that it was opened {fmtDate(e.openedNoticeAt)}">📬 told opened</span>
            {/if}
            {#if e.feedbackRating}
              <button class="badge sm feedback fb-btn" on:click={() => toggleViews(e.id)}
                title="Sender feedback{e.feedbackSubmittedAt ? ', sent ' + fmtDate(e.feedbackSubmittedAt) : ' (rating only, form not sent)'}:\n\n{feedbackLines(e.feedback).join('\n')}\n\nClick to open the access log">{FEEDBACK_EMOJI[e.feedbackRating] || '⭐'} feedback{#if e.feedback && (e.feedback.fiddly || e.feedback.wish)} 💬{/if}</button>
            {/if}
            {#if e.editCount}
              <span class="badge sm edited" title="Customer edited in cart {e.editCount === 1 ? 'once' : e.editCount + ' times'}, last {fmtDate(e.lastEditedAt)}">✏️ edited{e.editCount > 1 ? ' ×' + e.editCount : ''}</span>
            {/if}
            {#if e.reminderRequested}
              <span class="badge sm reminder" title="Customer opted in to a same-time-next-year reminder for {e.recipientName || 'this recipient'}">🔔 reminder</span>
            {/if}
            {#if e.thanksRepliedAt}
              <span class="badge sm replied" title="{e.thanksRecipientName || 'Recipient'} sent a thank-you reply {fmtDate(e.thanksRepliedAt)}{e.thanksMessage ? ':\n\n“' + e.thanksMessage + '”' : ''}">💌 replied</span>
            {/if}
            {#if e.storyPageCount}
              <span class="badge sm story" title="Story card with {e.storyPageCount} page{e.storyPageCount === 1 ? '' : 's'} + message. {e.storyPageMax ? (e.storyPageMax > e.storyPageCount ? 'Recipient reached the message page.' : 'Recipient reached page ' + e.storyPageMax + ' of ' + e.storyPageCount + '.') : 'Recipient has not turned a page yet.'}">📖 {e.storyPageMax ? (e.storyPageMax > e.storyPageCount ? 'read to the end' : e.storyPageMax + '/' + e.storyPageCount) : 'story'}</span>
            {/if}
            {#if e.leadCount}
              <span class="badge sm lead" title="{e.leadCount} lead-form submission{e.leadCount === 1 ? '' : 's'}, latest {fmtDate(e.lastLeadAt)} — open the access log to read them">🧲 {e.leadCount} lead{e.leadCount === 1 ? '' : 's'}</span>
            {/if}
            {#if e.recipientViewport}
              <span class="badge sm device" title="Recipient first opened on a {e.recipientViewport.class} ({e.recipientViewport.w}×{e.recipientViewport.h} viewport)">{e.recipientViewport.class === 'phone' ? '📱' : e.recipientViewport.class === 'tablet' ? '📟' : '💻'} {e.recipientViewport.class}</span>
            {/if}
            {#if e.thanksCodeEmailedAt}
              <span class="badge sm code" title="Code emailed to the recipient {fmtDate(e.thanksCodeEmailedAt)}">🎟️ code</span>
            {/if}
            {#if e.thanksNewsletterConfirmedAt}
              <span class="badge sm newsletter" title="Recipient confirmed the newsletter opt-in {fmtDate(e.thanksNewsletterConfirmedAt)} (subscribed in Shopify)">📣 confirmed</span>
            {:else if e.thanksNewsletter}
              <span class="badge sm newsletter pending" title="Recipient ticked 'keep me posted' — confirmation email sent, not yet clicked (pending in Shopify)">📣 pending</span>
            {/if}
          </td>
          <td>
            {#if e.attribution}
              <span class="badge src-{e.attribution.kind}" title={srcTitle(e.attribution)}>🍪 {e.attribution.channel}</span>
              {#if e.attribution.campaign}<div class="sub src-campaign" title={e.attribution.campaign}>{e.attribution.campaign}</div>{/if}
              {#if e.attribution.url}
                <div><span class="badge src-url" title={urlTitle(e.attribution.url)}>🔗 {e.attribution.url.channel}</span></div>
                {#if e.attribution.url.campaign && e.attribution.url.campaign !== e.attribution.campaign}
                  <div class="sub src-campaign" title={e.attribution.url.campaign}>{e.attribution.url.campaign}</div>
                {/if}
              {/if}
              {#if e.attribution.visit}
                <div><span class="badge src-visit" title={visitTitle(e.attribution.visit)}>👣 {e.attribution.visit.channel}</span></div>
                {#if e.attribution.visit.campaign && e.attribution.visit.campaign !== e.attribution.campaign}
                  <div class="sub src-campaign" title={e.attribution.visit.campaign}>{e.attribution.visit.campaign}</div>
                {/if}
              {/if}
              {#if e.attribution.url?.query || e.attribution.query || e.attribution.url?.keyword || e.attribution.term}
                <div class="sub src-keyword" title={`Ad keyword: ${e.attribution.url?.keyword || e.attribution.term || '—'}${e.attribution.url?.matchType ? ` (${{ e: 'exact', p: 'phrase', b: 'broad' }[e.attribution.url.matchType] || e.attribution.url.matchType})` : ''}`}>
                  🔍 {e.attribution.url?.query || e.attribution.query || e.attribution.url?.keyword || e.attribution.term}{#if e.attribution.url?.matchType} <span class="mt">{e.attribution.url.matchType}</span>{/if}
                </div>
              {/if}
            {:else}
              —
            {/if}
          </td>
          <td class="num">
            <button class="views-btn" class:active={expandedId === e.id} on:click={() => toggleViews(e.id)} title="Show access log">
              {#if e.viewCount > 0}<span class="dot"></span>{/if}{e.viewCount || 0}
              <span class="caret">{expandedId === e.id ? '▾' : '▸'}</span>
            </button>
            {#if e.viewCount > 0}<div class="sub">{e.uniqueVisitors ?? 0} unique</div>{/if}
          </td>
          <td class="sub">{fmtDate(e.paidAt)}</td>
          <td><button class="edit-btn" on:click={() => openEdit(e)} title="Edit ecard order">Edit</button></td>
        </tr>
        {#if expandedId === e.id}
          <tr class="exp">
            <td colspan="12">
              <EcardViews ecardId={e.id} viewCount={e.viewCount || 0} startOpen hideToggle />
            </td>
          </tr>
        {/if}
      {/each}
    </tbody>
    {/each}
  </table>
{/if}

<svelte:window on:keydown={(ev) => editing && ev.key === 'Escape' && closeEdit()} />

{#if editing}
  <div class="modal-backdrop" role="presentation" on:click|self={closeEdit}>
    <div class="modal" role="dialog" aria-modal="true">
      <div class="modal-head">
        <h3>Edit ecard</h3>
        <button class="x" on:click={closeEdit} aria-label="Close">✕</button>
      </div>

      <div class="modal-meta">
        <span class="mono-id">{editing.shortId || editing.id}</span>
        {#if editing.orderNumber}<span class="pill">{editing.orderNumber}</span>{/if}
        {#if editing.delivery}<span class="pill">{editing.delivery}</span>{/if}
        {#if editing.mode}<span class="pill">{editing.mode}</span>{/if}
        <span class="ds-tag {$datasource === 'staging' ? 'staging' : 'prod'}">{$datasource}</span>
      </div>

      {#if $datasource !== 'staging'}
        <p class="warn">⚠️ Editing a <strong>live production</strong> ecard — changes affect a real customer's card.</p>
      {/if}

      <label class="field">
        <span>Status</span>
        <select bind:value={editing.status}>
          {#each STATUSES as s}<option value={s}>{s}</option>{/each}
        </select>
      </label>

      {#if editing.delivery === 'email' || editing.delivery === 'sms'}
        <label class="field">
          <span>Recipient name</span>
          <input bind:value={editing.recipientName} maxlength="100" />
        </label>
      {/if}
      {#if editing.delivery === 'email'}
        <label class="field">
          <span>Recipient email</span>
          <input type="email" bind:value={editing.recipientEmail} maxlength="254" />
        </label>
      {:else if editing.delivery === 'sms'}
        <label class="field">
          <span>Recipient phone</span>
          <input bind:value={editing.recipientPhone} placeholder="+44…" />
        </label>
      {/if}
      {#if editing.delivery === 'email' || editing.delivery === 'sms'}
        <div class="row2">
          <label class="field">
            <span>Send date</span>
            <input type="date" bind:value={editing.sendDate} />
          </label>
          <label class="field">
            <span>Send time</span>
            <input type="time" bind:value={editing.sendTime} />
          </label>
        </div>
      {/if}

      {#if editing.mode === 'typed'}
        <label class="field">
          <span>Message</span>
          <textarea rows="4" maxlength="2000" bind:value={editing.message}></textarea>
        </label>
        <div class="row2">
          <label class="field">
            <span>Font</span>
            <input bind:value={editing.font} maxlength="100" />
          </label>
          <label class="field">
            <span>Font size</span>
            <input type="number" bind:value={editing.fontSize} min="8" max="120" />
          </label>
          <label class="field colour">
            <span>Text colour</span>
            <input type="color" bind:value={editing.textColour} />
          </label>
        </div>
      {:else if editing.mode === 'handwritten'}
        <label class="field colour">
          <span>Ink colour</span>
          <input type="color" bind:value={editing.inkColour} />
        </label>
      {/if}

      {#if editError}<p class="error">⚠️ {editError}</p>{/if}

      <div class="modal-actions">
        <button class="danger" on:click={deleteEcard} disabled={savingEdit || deleting} title="Permanently delete this ecard (test entries etc.) — asks for confirmation">{deleting ? 'Deleting…' : 'Delete ecard'}</button>
        <span class="spacer"></span>
        <button class="secondary" on:click={closeEdit} disabled={savingEdit || deleting}>Cancel</button>
        <button class="primary" on:click={saveEdit} disabled={savingEdit || deleting}>{savingEdit ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .sub.music { color: #b45309; font-weight: 500; }

  .head { display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
  .left { display: flex; align-items: center; gap: 1rem; }
  h2 { font-size: 1.25rem; margin: 0; }
  .count { display: inline-block; min-width: 1.4rem; text-align: center; font-size: .8rem; font-weight: 600; color: #555; background: #eee; border-radius: 999px; padding: .05rem .45rem; vertical-align: middle; }
  .search { padding: .35rem .55rem; border: 1px solid #ccc; border-radius: 6px; font-size: .85rem; min-width: 220px; }
  .sincebar { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; margin-top: .6rem; font-size: .82rem; }
  .sincebar-label { color: #666; font-weight: 600; font-size: .8rem; }
  .sincebar-meta { color: #555; font-variant-numeric: tabular-nums; }
  .presets { display: flex; gap: .25rem; flex-wrap: wrap; }
  .presets button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .2rem .55rem; border-radius: 999px; font-size: .76rem; }
  .presets button.ms { border-style: dashed; }
  .presets button.on { background: #2a5db0; color: #fff; border-color: #2a5db0; border-style: solid; }
  tr.day-head th { background: #f1f5f9; color: #334155; border-top: 2px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; padding: .4rem .8rem; font-size: .8rem; }
  tbody.day:first-of-type tr.day-head th { border-top: 0; }
  .day-title { font-weight: 700; }
  .day-count { margin-left: .6rem; font-weight: 500; color: #64748b; }
  button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .4rem .8rem; border-radius: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 1rem; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.05); }
  th, td { text-align: left; padding: .55rem .8rem; border-bottom: 1px solid #f0f0f0; font-size: .88rem; vertical-align: top; }
  th { background: #f7f7f7; font-weight: 600; }
  .num { text-align: right; }
  .mono { font-variant-numeric: tabular-nums; font-weight: 600; }
  .order-link { color: #2563eb; text-decoration: none; }
  .order-link:hover { text-decoration: underline; }
  .id-btn { display: inline-flex; align-items: center; gap: .35rem; cursor: pointer; border: 1px solid transparent; background: none; padding: .1rem .3rem; border-radius: 6px; font: inherit; color: inherit; }
  .id-btn:hover { border-color: #e9d5ff; background: #faf5ff; }
  .mono-id { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .75rem; max-width: 9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #555; }
  .copy-hint { font-size: .7rem; color: #9333ea; white-space: nowrap; }
  .preview { font-size: .75rem; color: #9333ea; text-decoration: none; }
  .preview:hover { text-decoration: underline; }
  .sub { color: #777; font-size: .8rem; }
  .pill { display: inline-block; background: #eef; color: #334; border-radius: 999px; padding: .05rem .5rem; font-size: .75rem; text-transform: uppercase; letter-spacing: .03em; }
  .badge { display: inline-block; border-radius: 6px; padding: .1rem .45rem; font-size: .78rem; font-weight: 600; text-transform: capitalize; }
  .badge.sm { font-size: .7rem; font-weight: 500; margin-left: .25rem; }
  .badge.ok { background: #dcfce7; color: #166534; }
  .badge.info { background: #dbeafe; color: #1e40af; }
  .badge.wait { background: #fef9c3; color: #854d0e; }
  .badge.bad { background: #fee2e2; color: #991b1b; }
  .badge.nudge { background: #fff7ed; color: #9a3412; }
  .badge.told { background: #ecfeff; color: #155e75; }
  .badge.toldsent { background: #eef2ff; color: #3730a3; }
  .badge.toldsent-err { background: #fef2f2; color: #b91c1c; }
  .badge.feedback { background: #f0fdf4; color: #166534; }
  .fb-btn { border: 0; cursor: pointer; font-family: inherit; }
  .fb-btn:hover { background: #dcfce7; }
  .badge.src-paid { background: #ede9fe; color: #5b21b6; }
  .badge.src-organic { background: #dcfce7; color: #166534; }
  .badge.src-referral { background: #ccfbf1; color: #115e59; }
  .badge.src-direct { background: #f3f4f6; color: #374151; }
  .badge.src-unknown { background: #f3f4f6; color: #9ca3af; font-weight: 500; }
  .src-campaign { max-width: 9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .src-keyword { max-width: 14rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #4b5563; }
  .src-keyword .mt { font-size: .7em; padding: 0 .3em; border-radius: .3em; background: #f3f4f6; border: 1px solid #e5e7eb; text-transform: uppercase; }
  .badge.src-url { background: #eff6ff; color: #1d4ed8; border: 1px dashed #bfdbfe; }
  .badge.tracked { background: #fdf4ff; color: #86198f; border: 1px dashed #e9d5ff; font-weight: 500; }
  .badge.url-tagged { background: #eff6ff; color: #1d4ed8; border: 1px dashed #bfdbfe; font-weight: 500; }
  .badge.src-visit { background: #f0fdf4; color: #15803d; border: 1px dashed #bbf7d0; }
  .badge.visit-tagged { background: #f0fdf4; color: #15803d; border: 1px dashed #bbf7d0; font-weight: 500; }
  .src-summary { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem; margin-top: .8rem; }
  .src-summary-label { font-size: .8rem; color: #666; font-weight: 600; }
  .src-summary .badge strong { font-weight: 700; margin-left: .15rem; }
  .badge.edited { background: #eff6ff; color: #1e40af; }
  .badge.reminder { background: #fefce8; color: #a16207; }
  .badge.replied { background: #fce7f3; color: #9d174d; }
  .badge.story { background: #eff6ff; color: #1e3a8a; }
  .badge.lead { background: #fdf2f8; color: #9d174d; border: 1px solid #fbcfe8; }
  .badge.device { background: #f8fafc; color: #475569; }
  .badge.funnel { background: #ecfdf5; color: #065f46; }
  .badge.code { background: #ecfdf5; color: #047857; }
  .badge.newsletter { background: #f0f9ff; color: #075985; }
  .badge.newsletter.pending { background: #f8fafc; color: #64748b; border: 1px dashed #cbd5e1; }
  .dot { display: inline-block; width: .5rem; height: .5rem; border-radius: 50%; background: #16a34a; margin-right: .3rem; vertical-align: middle; }
  .views-btn { cursor: pointer; border: 1px solid transparent; background: none; padding: .1rem .35rem; border-radius: 6px; font: inherit; color: inherit; font-variant-numeric: tabular-nums; }
  .views-btn:hover { border-color: #e9d5ff; background: #faf5ff; }
  .views-btn.active { border-color: #d8b4fe; background: #faf5ff; }
  .caret { color: #9333ea; margin-left: .2rem; font-size: .75rem; }
  tr.exp td { background: #faf7ff; padding: .5rem .8rem; }
  .muted { color: #666; }
  .muted code { background: #eee; padding: .1rem .3rem; border-radius: 4px; }
  .error { color: #b00020; }

  .edit-btn { border: 1px solid #d8b4fe; background: #faf5ff; color: #7e22ce; padding: .3rem .7rem; border-radius: 6px; font-size: .8rem; font-weight: 600; }
  .edit-btn:hover { background: #f3e8ff; }

  .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: flex-start; justify-content: center; padding: 4vh 1rem; z-index: 50; overflow-y: auto; }
  .modal { background: #fff; border-radius: 12px; width: 100%; max-width: 30rem; padding: 1.25rem 1.5rem 1.5rem; box-shadow: 0 12px 40px rgba(0,0,0,.25); }
  .modal-head { display: flex; justify-content: space-between; align-items: center; }
  .modal-head h3 { margin: 0; font-size: 1.15rem; }
  .x { border: none; background: none; font-size: 1rem; color: #888; cursor: pointer; padding: .2rem .4rem; }
  .x:hover { color: #333; }
  .modal-meta { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem; margin: .5rem 0 .25rem; }
  .modal-meta .mono-id { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .75rem; color: #555; }
  .ds-tag { font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; border-radius: 999px; padding: .1rem .5rem; }
  .ds-tag.prod { background: #fee2e2; color: #991b1b; }
  .ds-tag.staging { background: #dbeafe; color: #1e40af; }
  .warn { background: #fef3c7; border: 1px solid #fcd34d; color: #92400e; border-radius: 8px; padding: .5rem .7rem; font-size: .82rem; margin: .5rem 0; }
  .field { display: flex; flex-direction: column; gap: .25rem; margin-top: .7rem; font-size: .82rem; color: #444; }
  .field > span { font-weight: 600; }
  .field input, .field select, .field textarea { padding: .45rem .55rem; border: 1px solid #ccc; border-radius: 6px; font: inherit; font-size: .88rem; }
  .field.colour input[type="color"] { width: 3rem; height: 2.1rem; padding: .1rem; }
  .row2 { display: flex; gap: .6rem; }
  .row2 .field { flex: 1; }
  .modal-actions { display: flex; justify-content: flex-end; gap: .6rem; margin-top: 1.2rem; }
  .modal-actions button { padding: .5rem 1rem; border-radius: 6px; font-weight: 600; font-size: .85rem; }
  .modal-actions .secondary { border: 1px solid #ccc; background: #fff; color: #444; }
  .modal-actions .primary { border: 1px solid #7e22ce; background: #7e22ce; color: #fff; }
  .modal-actions .primary:disabled, .modal-actions .secondary:disabled { opacity: .6; cursor: default; }
  .modal-actions .danger { border: 1px solid #fecaca; background: #fff; color: #b91c1c; }
  .modal-actions .danger:hover:not(:disabled) { background: #fef2f2; border-color: #f87171; }
  .modal-actions .danger:disabled { opacity: .6; cursor: default; }
  .modal-actions .spacer { flex: 1; }
</style>
